import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { AppDispatch, RootState } from '../../../stateStore'
import { LoadingState, ValidationError } from '../../../types/commonTypes'
import { ApiLoadingState } from '../../../middleware/api/apiList'
import {
  AddRemittanceDetailsProps,
  EditRemittanceDetailsProps,
  GetCurrencyType,
  GetRemittanceDetailsType,
  RemittanceDetailsListState,
} from '../../../types/Invoicing/RemittanceDetails/remittanceDetailstype'
import remittanceDetailsListApi from '../../../middleware/api/Invoicing/RemittanceDetails/remittanceDetailsListApi'

const getRemittanceList = createAsyncThunk(
  'invoicing/getRemittanceList',
  async (_, thunkApi) => {
    try {
      return await remittanceDetailsListApi.getRemittanceList()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getCurrencyList = createAsyncThunk(
  'invoicing/getCurrencyList',
  async (currency: string, thunkApi) => {
    try {
      return await remittanceDetailsListApi.getCurrencyList(currency)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const deleteRemittanceList = createAsyncThunk(
  'invoicing/deleteRemittanceList',
  async (remittanceid: number, thunkApi) => {
    try {
      return await remittanceDetailsListApi.deleteRemittanceList(remittanceid)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const addCurrency = createAsyncThunk(
  'invoicing/addCurrency',
  async (currency: string, thunkApi) => {
    try {
      return await remittanceDetailsListApi.addCurrency(currency)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const deleteCurrency = createAsyncThunk(
  'invoicing/deleteCurrency',
  async (currencyId: number, thunkApi) => {
    try {
      return await remittanceDetailsListApi.deleteCurrency(currencyId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const addRemittanceDetails = createAsyncThunk<
  number | undefined,
  AddRemittanceDetailsProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'Invoicing/addRemittanceDetails',
  async (addRemittanceData: AddRemittanceDetailsProps, thunkApi) => {
    try {
      return await remittanceDetailsListApi.addRemittanceDetails(
        addRemittanceData,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const editRemittanceDetails = createAsyncThunk<
  number | undefined,
  EditRemittanceDetailsProps,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'Invoicing/editRemittanceDetails',
  async (editRemittanceData: EditRemittanceDetailsProps, thunkApi) => {
    try {
      return await remittanceDetailsListApi.editRemittanceDetails(
        editRemittanceData,
      )
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
export const initialRemittanceState: RemittanceDetailsListState = {
  getRemittanceDetailsList: {} as GetRemittanceDetailsType,
  getCurrencyList: [],
  isLoading: ApiLoadingState.idle,
}
const remittanceDetailsSlice = createSlice({
  name: 'invoicing',
  initialState: initialRemittanceState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(getCurrencyList.fulfilled, (state, action) => {
      state.getCurrencyList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(getRemittanceList.fulfilled, (state, action) => {
      state.getRemittanceDetailsList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(addCurrency.fulfilled, (state, action) => {
      state.getRemittanceDetailsList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addCase(deleteCurrency.fulfilled, (state, action) => {
      state.getCurrencyList = action.payload
      state.isLoading = ApiLoadingState.succeeded
    })
    builder.addMatcher(
      isAnyOf(
        getCurrencyList.pending,
        getRemittanceList.pending,
        addCurrency.pending,
        deleteCurrency.pending,
      ),
      (state) => {
        state.isLoading = ApiLoadingState.loading
      },
    )
  },
})
const remittanceThunk = {
  getCurrencyList,
  deleteRemittanceList,
  getRemittanceList,
  addRemittanceDetails,
  editRemittanceDetails,
  addCurrency,
  deleteCurrency,
}
const isLoading = (state: RootState): LoadingState =>
  state.remittanceList.isLoading
const remittances = (state: RootState): GetRemittanceDetailsType =>
  state.remittanceList.getRemittanceDetailsList
const currencies = (state: RootState): GetCurrencyType[] =>
  state.remittanceList.getCurrencyList

const remittanceSelectors = {
  isLoading,
  remittances,
  currencies,
}
export const remittanceService = {
  ...remittanceThunk,
  actions: remittanceDetailsSlice.actions,
  selectors: remittanceSelectors,
}
export default remittanceDetailsSlice.reducer
